import { useEffect, useMemo, useState } from 'react';
import type { User } from '../../api/users';
import { getUserById } from '../../api/users';
import { legacyImageUrlsMapper } from '../../api/legacyCommonMappers';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { selectUserId } from '../Login/LoginSlice';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { UserAction } from '../../components/ActionButtons/ActionButtons';
import ActionButtons from '../../components/ActionButtons/ActionButtons';
import { setSelectedUsersAction } from '../CreateMessage/createMessageSlice/actionCreators';
import type { MemberSettingsOptionsStateType } from '../MemberSettings/MemberSettings';
import {
  SAvailabilityChipContainer,
  SPage,
  SProfilePictureWrapper,
  SSmallText,
  SUserName,
} from '../MemberSettings/MemberSettings';
import { ProfilePicture } from '../../components/ProfilePicture/ProfilePicture';
import type { DynamicFormFieldType } from '../../components/MemberSettingsDynamicForm/MemberSettingsDynamicForm';
import MemberSettingsDynamicForm from '../../components/MemberSettingsDynamicForm/MemberSettingsDynamicForm';
import { getNumbersBasedDateTextByLanguage } from '../../utils/date';
import { translate } from '../../utils/translate';
import { isNil, isEmpty } from 'lodash';
import Loader from '../../components/Loader/Loader';
import {
  handleMemberSettingsEmailClick,
  handleMemberSettingsPhoneClick,
} from '../MemberSettings/helpers';
import Options from '../../components/Options/Options';
import { resetAll, setActiveTab, setSelectedUsers } from '../CreateMessage/createMessageSlice';
import { ActiveTab } from '../../utils/enums';
import { batch } from 'react-redux';
import styled from 'styled-components';
import type { SelectListUser } from '../CreateMessage/createMessageSlice/types';
import AvailabilityChip from '../../components/AvailabilityChip/AvailabilityChip';
import { mapUserToSelectListUser } from './helpers';
import { checkIsAvailable } from '../../utils/helperFunctions';
import { useTenantId } from '../../utils/customHooks/useTenantId';

const SMemberSettingsDynamicFormWrapper = styled.div`
  margin: 1rem 0 !important;
`;

const SFlexDiv = styled.div`
  flex: 1;
`;

function Member() {
  const { memberID } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const authUserId = useAppSelector(selectUserId);
  const tenantId = useTenantId();

  const [isUserProfile, setIsUserProfile] = useState(false);
  const [member, setMember] = useState<User | null>(null);
  const [optionsState, setOptionsState] = useState<MemberSettingsOptionsStateType>({
    isOpen: false,
  });

  const isAvailable = useMemo(() => checkIsAvailable(member?.unavailableUntil ?? null), [member]);

  const getMember = async () => {
    if (!tenantId) {
      throw new Error('Tenant missing');
    }
    const userResult = await getUserById({ userId: Number(memberID), tenantId });
    setMember(userResult.data?.data ?? null);
  };

  useEffect(() => {
    if (`${authUserId}` === memberID) {
      setIsUserProfile(true);
    }

    getMember();
    return () => {
      setOptionsState({ isOpen: false });
    };
  }, [tenantId]);

  if (!member) return <Loader />;

  const addUserToCreateMessage = (selectListUser: SelectListUser) =>
    new Promise<void>(resolve => {
      batch(() => {
        dispatch(resetAll());
        dispatch(setActiveTab(ActiveTab.Users));
        dispatch(setSelectedUsers([selectListUser]));
      });
      resolve();
    });

  const actions: Record<string, UserAction[]> = {
    member: [
      {
        label: 'messages_createMessage',
        fireAction: async () => {
          await addUserToCreateMessage(
            mapUserToSelectListUser(member, { isSelected: true, admin: false })
          );
          navigate(`/createMessage/new`, {
            state: { prevPath: location.pathname, disableRecipientsChange: true },
          });
        },
      },
    ],
    ownProfile: [
      {
        label: 'groups_editProfile',
        fireAction: () => {
          dispatch(
            setSelectedUsersAction([
              mapUserToSelectListUser(member, {
                isSelected: true,
                title: null,
                photoFileName: null,
                email: '',
                creatorId: null,
              }),
            ])
          );
          navigate(`/profile`);
        },
      },
    ],
  };

  const checkIfNotNullOrEmpty = (value?: string | null) => {
    if (isNil(value)) {
      return false;
    } else if (isEmpty(value)) {
      return false;
    } else {
      return true;
    }
  };

  const setOptionsOpen = (isOpen: boolean) => {
    setOptionsState(prev => ({ ...prev, isOpen }));
  };

  const emailField: DynamicFormFieldType = {
    visible: checkIfNotNullOrEmpty(member?.email),
    placeHolder: 'Email Address ',
    value: member?.email || '',
    onClick: handleMemberSettingsEmailClick(member?.email || '', setOptionsState, setOptionsOpen),
  };

  const numberField: DynamicFormFieldType = {
    visible: checkIfNotNullOrEmpty(member?.phoneNumbers.primary),
    placeHolder: 'Number Field ',
    value: member?.phoneNumbers.primary || '',
    onClick: handleMemberSettingsPhoneClick(
      member?.phoneNumbers.primary || '',
      setOptionsState,
      setOptionsOpen
    ),
  };

  const numberField2: DynamicFormFieldType = {
    visible: checkIfNotNullOrEmpty(member?.phoneNumbers.secondary),
    placeHolder: 'Number Field ',
    value: member?.phoneNumbers.secondary || '',
    onClick: handleMemberSettingsPhoneClick(
      member?.phoneNumbers.secondary || '',
      setOptionsState,
      setOptionsOpen
    ),
  };

  const additionalInfoField: DynamicFormFieldType = {
    visible: checkIfNotNullOrEmpty(member?.title),
    placeHolder: 'additionalInfo  ',
    value: member?.title || '',
  };

  return (
    <SPage>
      <SProfilePictureWrapper role="profilePictureWrapper">
        <ProfilePicture
          profilePictureFileName={legacyImageUrlsMapper.fromCoreToLegacy(member.imageUrls)}
          diameter={112}
        />
      </SProfilePictureWrapper>
      <SUserName role="userName">{member.name}</SUserName>
      <SAvailabilityChipContainer>
        <AvailabilityChip isAvailable={isAvailable} />
      </SAvailabilityChipContainer>
      <SMemberSettingsDynamicFormWrapper>
        <MemberSettingsDynamicForm
          fields={[additionalInfoField, emailField, numberField, numberField2]}
        />
      </SMemberSettingsDynamicFormWrapper>
      <SSmallText role="lastVisited">
        {translate(`groups_lastVisited`)}{' '}
        {getNumbersBasedDateTextByLanguage({ dateText: member?.lastAccessAt })}
      </SSmallText>
      <SFlexDiv />
      <ActionButtons actions={isUserProfile ? actions.ownProfile : actions?.member} />
      {optionsState.options && (
        <Options
          items={optionsState.options}
          isOpen={optionsState.isOpen}
          setIsOpen={setOptionsOpen}
          setTabBar
        />
      )}
    </SPage>
  );
}

export default Member;
